import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { sortedListRequest } from './common/list.js';
import { paginated } from './common/pagination.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

export const roleSummarySchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  requiresTwoFactor: z.boolean(),
  permissionCount: z.number().int().min(0),
  userCount: z.number().int().min(0),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const roleSchema = roleSummarySchema.extend({
  permissions: z.array(z.string()),
});

export type RoleSummary = z.infer<typeof roleSummarySchema>;
export type Role = z.infer<typeof roleSchema>;

/**
 * English, lowercase and stable, because it is an identifier the seed, the
 * tests and the audit trail all name. It is set once at creation and never
 * appears on `update` — renaming a role means changing `name`, not `code`.
 */
const codeSchema = z
  .string()
  .trim()
  .min(3)
  .max(50)
  .regex(/^[a-z][a-z0-9_]*$/);

const nameSchema = z.string().trim().min(2).max(120);
const descriptionSchema = z.string().trim().max(500);
const permissionsSchema = z.array(z.string().trim().min(3).max(100)).max(500);

export const roleKindSchema = z.enum(['all', 'system', 'custom']);

/**
 * `userCount` is an aggregate of the same grouped query the list already runs,
 * so it sorts without a second read. Roles are a bounded dictionary — tens of
 * rows — which is why none of these needs an index of its own.
 */
export const ROLE_SORT_FIELDS = ['name', 'code', 'userCount', 'createdAt'] as const;

export type RoleSortField = (typeof ROLE_SORT_FIELDS)[number];

export const roleContract = {
  list: oc
    .route({ method: 'GET', path: '/roles', summary: 'Roles and how many users hold each' })
    .errors(guarded)
    .input(
      sortedListRequest(ROLE_SORT_FIELDS, 'name', 'asc').extend({
        kind: roleKindSchema.default('all'),
      }),
    )
    .output(paginated(roleSummarySchema)),

  get: oc
    .route({ method: 'GET', path: '/roles/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ role: roleSchema })),

  create: oc
    .route({ method: 'POST', path: '/roles' })
    .errors(guarded)
    .input(
      z.object({
        code: codeSchema,
        name: nameSchema,
        description: descriptionSchema.nullable().default(null),
        requiresTwoFactor: z.boolean().default(false),
        permissions: permissionsSchema.default([]),
      }),
    )
    .output(z.object({ role: roleSchema })),

  update: oc
    .route({ method: 'PATCH', path: '/roles/{id}' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        name: nameSchema.optional(),
        description: descriptionSchema.nullable().optional(),
        requiresTwoFactor: z.boolean().optional(),
        permissions: permissionsSchema.optional(),
      }),
    )
    .output(z.object({ role: roleSchema })),

  /**
   * Deletion rather than archival: a role holds no history of its own, so
   * unlike a user there is nothing to preserve. What must be preserved is the
   * reference — a role any user still holds, and every seeded system role, is
   * refused with a reason the client translates.
   */
  delete: oc
    .route({ method: 'DELETE', path: '/roles/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ id: z.uuid(), deleted: z.literal(true) })),
};
